import { RAW_MATERIAL_KIND_LABELS } from "@artisancode/api-types";
import { AlertCircle } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { TableCell, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

import {
  CountedQtyField,
  OpnameDiffBadge,
  ReasonSelect,
  RemoveItemButton,
  RepackingCountField,
  type OpnameItemProps,
} from "./stock-opname-item-fields";
import { getOpnameItemState, opnameBuckets } from "./stock-opname-utils";

/** Desktop table row of one counted line. */
export function StockOpnameItemRow(props: OpnameItemProps) {
  const {
    item,
    reasons,
    isRawMaterial,
    isLocked,
    onOnHandChange,
    onReasonChange,
    onNoteChange,
    onRemove,
    error,
  } = props;
  const {
    hasDiscrepancy,
    applicableReasons,
    selectedReasonId,
    isMissingReason,
  } = getOpnameItemState(item, reasons, isRawMaterial);

  const { onHand: hasOnHand, repacking: hasRepacking } = opnameBuckets(
    isRawMaterial,
    item.kind,
  );

  return (
    <TableRow
      className={cn(
        "text-xs transition-colors",
        hasDiscrepancy && "bg-amber-50/40 dark:bg-amber-950/10",
        error && "bg-destructive/5",
      )}
    >
      <TableCell className="py-2.5 font-medium">
        <div className="max-w-[200px] truncate font-semibold text-foreground">
          {item.itemName}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
          {item.itemSku && <span className="font-mono">{item.itemSku}</span>}
          {item.kind && (
            <Badge variant="outline" className="px-1 py-0 text-[9px]">
              {RAW_MATERIAL_KIND_LABELS[item.kind]}
            </Badge>
          )}
          {item.categoryName && <span>• {item.categoryName}</span>}
        </div>
      </TableCell>

      <TableCell className="font-mono text-muted-foreground">
        {item.uomName}
      </TableCell>

      <TableCell className="text-right font-mono font-medium">
        {hasOnHand ? (
          item.systemOnHand.toLocaleString()
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-right">
        {hasOnHand ? (
          <CountedQtyField
            value={item.countedOnHand}
            isLocked={isLocked}
            invalid={!!error?.onHand}
            onChange={(v) => onOnHandChange(item.id, v)}
            className="ml-auto h-8 w-24"
          />
        ) : (
          <span className="text-muted-foreground font-mono">—</span>
        )}
      </TableCell>
      <TableCell className="text-center">
        {hasOnHand ? (
          <OpnameDiffBadge diff={item.diffOnHand} />
        ) : (
          <span className="text-muted-foreground font-mono">—</span>
        )}
      </TableCell>

      {isRawMaterial && (
        <>
          <TableCell className="border-l pl-3 text-right font-mono font-medium">
            {hasRepacking ? (
              item.systemRepacking.toLocaleString()
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </TableCell>
          <TableCell className="text-right">
            {hasRepacking ? (
              <div className="flex items-center justify-end gap-1">
                <RepackingCountField {...props} inputClassName="h-8 w-20" />
              </div>
            ) : (
              <span className="text-muted-foreground font-mono">—</span>
            )}
          </TableCell>
          <TableCell className="text-center">
            {hasRepacking ? (
              <OpnameDiffBadge diff={item.diffRepacking} />
            ) : (
              <span className="text-muted-foreground font-mono">—</span>
            )}
          </TableCell>
        </>
      )}

      <TableCell className="py-2">
        <div className="flex flex-col gap-1.5">
          {hasDiscrepancy && (
            <div className="flex items-center gap-1.5">
              {isLocked ? (
                <span className="text-xs font-medium text-foreground">
                  {item.reasonName || "-"}
                </span>
              ) : (
                <ReasonSelect
                  reasons={applicableReasons}
                  value={selectedReasonId}
                  invalid={isMissingReason || !!error?.reason}
                  onChange={(id) => onReasonChange(item.id, id)}
                  className="h-7"
                />
              )}
              {(isMissingReason || error?.reason) && (
                <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
              )}
            </div>
          )}

          {isLocked ? (
            item.note && (
              <span className="text-[11px] italic text-muted-foreground">
                {item.note}
              </span>
            )
          ) : (
            <Input
              value={item.note || ""}
              onChange={(e) => onNoteChange(item.id, e.target.value)}
              placeholder="Catatan tambahan (opsional)..."
              className="h-7 text-[11px]"
            />
          )}
        </div>
      </TableCell>

      {!isLocked && onRemove && (
        <TableCell className="w-10 pr-2">
          <RemoveItemButton
            itemName={item.itemName}
            onClick={() => onRemove(item.id)}
          />
        </TableCell>
      )}
    </TableRow>
  );
}
